import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getRoleDisplayName, getRoleColorClass } from '../../utils/roleUtils';
import { 
  LogOut, 
  User, 
  Menu, 
  X,
  Moon,
  Sun
} from 'lucide-react';

const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    // localStorage'dan tercihi al, yoksa varsayılan olarak açık mod
    return localStorage.getItem('darkMode') === 'true';
  });

  useEffect(() => {
    // Karanlık mod değiştiğinde HTML elementine dark sınıfını ekle/kaldır
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Tercihi localStorage'a kaydet
    localStorage.setItem('darkMode', darkMode);
    
    // Diğer bileşenlere değişikliği bildir (özel event)
    window.dispatchEvent(new Event('darkModeChange'));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="bg-primary-700 dark:bg-gray-900 text-white shadow-md">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo ve Başlık */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
              <h1 className="text-xl font-bold hidden md:block">CSBM İade Takip Sistemi</h1>
            </Link>
          </div>

          {/* Mobil Menü Butonu */}
          <div className="md:hidden flex items-center">
            {/* Karanlık Mod Butonu - Mobil */}
            <button 
              onClick={toggleDarkMode}
              className="p-2 mr-2 focus:outline-none focus:bg-primary-600 dark:focus:bg-gray-700 rounded"
              aria-label={darkMode ? 'Açık moda geç' : 'Karanlık moda geç'}
            >
              {darkMode ? (
                <Sun size={20} className="text-yellow-300" />
              ) : (
                <Moon size={20} className="text-white" />
              )}
            </button>

            <button 
              onClick={toggleMobileMenu}
              className="p-2 focus:outline-none focus:bg-primary-600 dark:focus:bg-gray-700 rounded"
            >
              {mobileMenuOpen ? (
                <X size={24} className="text-white" />
              ) : (
                <Menu size={24} className="text-white" />
              )}
            </button>
          </div>

          {/* Masaüstü Navigasyon */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Karanlık Mod Butonu - Masaüstü */}
            <button 
              onClick={toggleDarkMode}
              className="p-2 focus:outline-none hover:bg-primary-600 dark:hover:bg-gray-700 rounded transition-colors"
              aria-label={darkMode ? 'Açık moda geç' : 'Karanlık moda geç'}
            >
              {darkMode ? (
                <Sun size={20} className="text-yellow-300" />
              ) : (
                <Moon size={20} className="text-white" />
              )}
            </button>

            {/* Kullanıcı Bilgileri ve Dropdown */}
            <div className="relative">
              <button 
                onClick={toggleDropdown} 
                className="flex items-center space-x-2 p-2 hover:bg-primary-600 dark:hover:bg-gray-700 rounded"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white text-primary-700 dark:bg-gray-700 dark:text-white font-bold">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium">{user?.full_name || user?.username}</div>
                  <div className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleColorClass(user?.role)}`}>
                    {getRoleDisplayName(user?.role)}
                  </div>
                </div>
              </button>

              {/* Dropdown Menü */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-10">
                  <Link 
                    to="/profile" 
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <User size={16} className="mr-2" />
                    Profil
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <LogOut size={16} className="mr-2" />
                    Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobil Menü */}
        {mobileMenuOpen && (
          <div className="mt-4 md:hidden">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/dashboard" 
                className="block px-3 py-2 rounded hover:bg-primary-600 dark:hover:bg-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                Ana Sayfa
              </Link>
              <Link 
                to="/refunds" 
                className="block px-3 py-2 rounded hover:bg-primary-600 dark:hover:bg-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                İade İşlemleri
              </Link>
              <Link 
                to="/profile" 
                className="block px-3 py-2 rounded hover:bg-primary-600 dark:hover:bg-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                Profil
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 rounded hover:bg-primary-600 dark:hover:bg-gray-700 text-left"
              >
                <LogOut size={16} className="mr-2" />
                Çıkış Yap
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;